import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import storage from '../services/storage';
import api from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const userRef = useRef(null);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const token = await storage.getItem('token');
                const userInfo = await storage.getItem('user');

                if (token && userInfo) {
                    const parsedUser = JSON.parse(userInfo);
                    userRef.current = parsedUser;
                    setUser(parsedUser);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, []);

    const persistUser = useCallback(async (nextUser) => {
        userRef.current = nextUser;
        if (nextUser) {
            await storage.setItem('user', JSON.stringify(nextUser));
        } else {
            await storage.deleteItem('user');
        }
        setUser(nextUser);
    }, []);

    const refreshPartner = useCallback(async () => {
        let currentUser = userRef.current;
        if (!currentUser) {
            const cachedUser = await storage.getItem('user');
            if (cachedUser) {
                try {
                    currentUser = JSON.parse(cachedUser);
                    userRef.current = currentUser;
                } catch (error) {
                    currentUser = null;
                }
            }
        }

        if (!currentUser) return;

        try {
            const response = await api.get('/user/partner');
            const partnerData = response.data;
            const updatedUser = {
                ...currentUser,
                partnerId: partnerData.id,
                partnerNickname: partnerData.nickname,
                partnerUsername: partnerData.username || partnerData.partnerUsername || null,
                partnerRole: partnerData.role,
                partnerAvatar: partnerData.avatar,
                partnerRelationshipStatus: partnerData.relationship_status,
                relationshipStatus: partnerData.myRelationshipStatus || currentUser.relationshipStatus
            };
            await persistUser(updatedUser);
        } catch (error) {
            if (error?.response?.status === 404) {
                const updatedUser = {
                    ...currentUser,
                    partnerId: null,
                    partnerNickname: null,
                    partnerUsername: null,
                    partnerRole: null,
                    partnerAvatar: null,
                    partnerRelationshipStatus: null,
                };
                await persistUser(updatedUser);
            }
        }
    }, [persistUser]);

    useEffect(() => {
        if (!user?.id) return undefined;

        refreshPartner();
        const interval = setInterval(() => {
            refreshPartner();
        }, 5000);

        return () => clearInterval(interval);
    }, [user?.id, refreshPartner]);

    const login = async (username, password) => {
        try {
            const response = await api.post('/auth/login', { username, password });
            const { token, ...userData } = response.data;

            await storage.setItem('token', token);
            await persistUser(userData);
            return { success: true };
        } catch (error) {
            console.error(error);
            return { success: false, error: error.response?.data?.message || '登录失败' };
        }
    };

    const register = async (username, password, role, partnerCode, nickname) => {
        try {
            console.log('AuthContext register called with:', { username, role, partnerCode, nickname });
            const response = await api.post('/auth/register', { username, password, role, partnerCode, nickname });
            console.log('AuthContext register response:', response.data);
            const { token, ...userData } = response.data;

            await storage.setItem('token', token);
            await persistUser(userData);
            return { success: true };
        } catch (error) {
            console.error('AuthContext register error:', error);
            const errorMessage = error.response?.data?.message || error.message || '注册失败';
            return { success: false, error: errorMessage };
        }
    };

    const logout = async () => {
        await storage.deleteItem('token');
        await persistUser(null);
    };

    const updateUser = async (data) => {
        const updatedUser = { ...(userRef.current || {}), ...data };
        await persistUser(updatedUser);
        return updatedUser;
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refreshPartner, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};
